import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { Loader2, Check, Save, Sparkles, Brain, MessageSquare, User as UserIcon, Shield, Zap, Database } from 'lucide-react';
import { AIProviderSettings } from '../components/settings/AIProviderSettings';

export default function AISettings({ hideHeader = false }: { hideHeader?: boolean }) {
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Local state for Intelligence Hub
  const [aiName, setAiName] = useState('OS Bot');
  const [aiCustomPrompt, setAiCustomPromptState] = useState('');
  const [aiTone, setAiToneLocal] = useState('Professional');
  const [userAlias, setUserAliasLocal] = useState('');
  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const [showWidget, setShowWidget] = useState(false);
  const [contributeShared, setContributeShared] = useState(true);
  const [useSharedForLocal, setUseSharedForLocal] = useState(true);
  
  const { 
    aiName: storeAiName, 
    setAiName: setStoreAiName,
    aiCustomPrompt: storeAiCustomPrompt,
    setAiCustomPrompt: setStoreAiCustomPrompt,
    aiTone: storeAiTone,
    setAiTone: setStoreAiTone,
    aiUserAlias: storeUserAlias,
    setAiUserAlias: setStoreAiUserAlias,
    currentUser, 
    showFloatingAIWidget,
    setShowFloatingAIWidget,
    contributeToSharedMemory,
    setContributeToSharedMemory,
    useSharedMemoryForLocalAI,
    setUseSharedMemoryForLocalAI,
    syncSettingsToSupabase
  } = useStore();

  useEffect(() => {
    async function loadSettings() {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      // Load from store (which bootstraps from Supabase)
      setAiName(storeAiName || 'OS Bot');
      setAiCustomPromptState(storeAiCustomPrompt || '');
      setAiToneLocal(storeAiTone || 'Professional');
      setUserAliasLocal(storeUserAlias || '');
      
      setShowWidget(showFloatingAIWidget);
      setContributeShared(contributeToSharedMemory);
      setUseSharedForLocal(useSharedMemoryForLocalAI);

      setLoading(false);
    }

    loadSettings();
    document.title = 'WholeScale OS';
  }, [currentUser, storeAiName, storeAiCustomPrompt, storeAiTone, storeUserAlias, showFloatingAIWidget, contributeToSharedMemory, useSharedMemoryForLocalAI]);

  const handleSaveAll = async () => {
    if (!currentUser?.id) return;
    setSaving(true);

    try {
      // 1. Update Zustand Store (Local-First)
      setStoreAiName(aiName.trim());
      setStoreAiCustomPrompt(aiCustomPrompt.trim());
      setStoreAiTone(aiTone.trim());
      setStoreAiUserAlias(userAlias.trim());
      setShowFloatingAIWidget(showWidget);
      setContributeToSharedMemory(contributeShared);
      setUseSharedMemoryForLocalAI(useSharedForLocal);

      // 2. Sync to Supabase
      await syncSettingsToSupabase();

      // 3. Local persistence fallback
      localStorage.setItem('wholescale-ai-name', aiName);
      localStorage.setItem('wholescale-ai-custom-prompt', aiCustomPrompt);
      localStorage.setItem('wholescale-ai-tone', aiTone);
      localStorage.setItem('wholescale-user-alias', userAlias);
      localStorage.setItem('user_show_floating_widget', showWidget.toString());

      setSaveSuccess(true);
      toast.success('Intelligence Hub settings updated');
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      console.error('Failed to save AI settings:', err);
      toast.error('Failed to sync settings to cloud.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--t-primary)]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-20 p-6">
      {!hideHeader && (
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight text-[var(--t-text)] flex items-center gap-2">
            <Brain className="w-6 h-6 text-[var(--t-primary)]" />
            Intelligence Hub
          </h1>
          <p className="text-[var(--t-text-muted)] text-sm">
            Configure how WholeScale OS Bot interacts with you and your data.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Core Persona */}
        <div className="p-6 rounded-xl border border-[var(--t-border)] bg-[var(--t-surface)] space-y-6">
          <div className="flex items-center gap-3 border-b border-[var(--t-border)] pb-4">
            <div className="p-2 rounded-lg bg-[var(--t-primary)]/10 text-[var(--t-primary)]">
              <Sparkles size={20} />
            </div>
            <h2 className="text-lg font-semibold text-[var(--t-text)]">Identity & Persona</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)]">Bot Name</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                <input
                  type="text"
                  value={aiName}
                  onChange={(e) => setAiName(e.target.value)}
                  className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-[var(--t-primary)] outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)]">User Nickname</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                <input
                  type="text"
                  value={userAlias}
                  onChange={(e) => setUserAliasLocal(e.target.value)}
                  className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-[var(--t-primary)] outline-none transition-all"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)]">Interaction Tone</label>
            <select
              value={aiTone}
              onChange={(e) => setAiToneLocal(e.target.value)}
              className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-[var(--t-primary)] outline-none appearance-none cursor-pointer"
            >
              {['Professional', 'Friendly', 'Direct', 'Sassy', 'Aggressive', 'Humorous', 'Custom'].map(tone => (
                <option key={tone} value={tone} className="bg-[var(--t-bg)]">{tone}</option>
              ))}
            </select>
          </div>

          {aiTone === 'Custom' && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)]">Custom Instructions</label>
              <textarea
                value={aiCustomPrompt}
                onChange={(e) => setAiCustomPromptState(e.target.value)}
                className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[var(--t-primary)] outline-none min-h-[80px] resize-none"
              />
            </div>
          )}
        </div>

        {/* System Capabilities */}
        <div className="p-6 rounded-xl border border-[var(--t-border)] bg-[var(--t-surface)] space-y-6">
          <div className="flex items-center gap-3 border-b border-[var(--t-border)] pb-4">
            <div className="p-2 rounded-lg bg-[var(--t-warning)]/10 text-[var(--t-warning)]">
              <Zap size={20} />
            </div>
            <h2 className="text-lg font-semibold text-[var(--t-text)]">System & Privacy</h2>
          </div>

          <div className="space-y-4">
            {[
             { label: 'Persistent Context', desc: 'Bot remembers past interactions', icon: Database, value: memoryEnabled, setter: setMemoryEnabled },
              { label: 'Floating Widget', desc: 'Show bot throughout the OS', icon: Sparkles, value: showWidget, setter: (val: boolean) => { setShowWidget(val); setShowFloatingAIWidget(val); } },
              { label: 'Contribute to Shared Memory', desc: 'Anonymously share AI insights with the team', icon: Shield, value: contributeShared, setter: setContributeShared },
              { label: 'Use Shared Memory for Local AI', desc: 'Enhance local models with collective knowledge', icon: Zap, value: useSharedForLocal, setter: setUseSharedForLocal },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 rounded-lg bg-[var(--t-background)] border border-[var(--t-border)]">
                <div className="flex items-center gap-3">
                  <item.icon className="w-4 h-4 text-[var(--t-text-muted)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--t-text)]">{item.label}</p>
                    <p className="text-[10px] text-[var(--t-text-muted)]">{item.desc}</p>
                  </div>
                </div>
                <div 
                  onClick={() => item.setter(!item.value)}
                  className={`toggle-switch ${item.value ? 'is-on' : 'is-off'}`}
                >
                  <div className="toggle-knob" />
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--t-background)] border border-[var(--t-border)] opacity-60">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-[var(--t-text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--t-text)]">Local-First Privacy</p>
                  <p className="text-[10px] text-[var(--t-text-muted)]">Encrypted intent processing</p>
                </div>
              </div>
              <div className="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-[var(--t-success)]/10 text-[var(--t-success)]">Active</div>
            </div>
          </div>

          <button 
            onClick={() => toast.success('Neural cache cleared')}
            className="w-full py-2 text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] hover:text-[var(--t-error)] transition-colors border border-dashed border-[var(--t-border)] rounded-lg"
          >
            Purge Neural Cache
          </button>
        </div>
      </div>

      <div className="relative">
        <AIProviderSettings />
        
        {/* Floating Save Button Area */}
        <div className="mt-12 flex flex-col items-center gap-4">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="w-full max-w-md flex items-center justify-center gap-3 py-4 bg-[var(--t-primary)] text-white font-black rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:-translate-y-1 active:scale-[0.98] transition-all disabled:opacity-50 text-lg uppercase tracking-tighter"
          >
            {saving ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                {saveSuccess ? <Check className="w-6 h-6" /> : <Save className="w-6 h-6" />}
                {saveSuccess ? 'Intelligence Synced' : 'Synchronize All Intelligence'}
              </>
            )}
          </button>
          <p className="text-[10px] text-[var(--t-text-muted)] font-bold uppercase tracking-[0.2em]">
            State persisted to encrypted cloud storage
          </p>
        </div>
      </div>
    </div>
  );
}
